import { faEnvelope, faLocationDot, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useEffect, useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { useUserParams } from "../hooks/useUserParams";

export default function Contact() {
  const [popup, setPopup] = useState(false);
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
  } = useUserParams();
  const [formData, setFormData] = useState({
    Name: "",
    "Email Address": "",
    Message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function submitForm(event) {
    event.preventDefault();

    const data = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Contact Form Message",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const response = await res.json();

      if (response?.success) {
        console.log("Success", response);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          Name: "",
          "Email Address": "",
          Message: "",
        });
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
  }

  const seoConfig = pageSEO.contact();
  return (
    <>
      <SEO {...seoConfig} />

      <section className="relative pt-36 pb-24 overflow-hidden">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="relative z-10 max-w-[1600px] mx-auto px-6"
        >
          <div className="mb-14">
            <motion.div variants={fadeUp} className="mb-5">
              <p className="uppercase tracking-[0.4em] font-medium text-cottage-primary">
                Start a Conversation
              </p>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="font-serif text-5xl lg:text-6xl leading-none text-cottage-primary"
            >
              Get In Touch
            </motion.h2>
          </div>

          <motion.div
            variants={fadeUp}
            className="grid gap-6 lg:grid-cols-[1.6fr_1fr]"
          >
            <div className="rounded-lg bg-white p-8 lg:p-12 shadow-[0_30px_80px_rgba(15,23,42,0.08)] border border-cottage-accent">

              <div className="mb-10">
                <h2 className="font-serif text-3xl text-cottage-primary">
                  Send A Message
                </h2>
                <p className="mt-3 text-sm text-cottage-muted">
                  We will get back to you as soon as possible.
                </p>
              </div>

              <form onSubmit={submitForm} className="space-y-6">

                <div>
                  <label className="mb-2 block text-lg uppercase tracking-[0.3em] text-cottage-primary">
                    Full Name
                  </label>
                  <input
                    name="Name"
                    value={formData.Name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    className="w-full rounded-lg border border-cottage-accent bg-cottage-secondary/20 px-5 py-4 text-harbour-navy outline-none transition focus:border-harbour-accent focus:bg-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-lg uppercase tracking-[0.3em] text-cottage-primary">
                    Email Address
                  </label>
                  <input
                    name="Email Address"
                    value={formData["Email Address"]}
                    onChange={handleInputChange}
                    placeholder="john@email.com"
                    className="w-full rounded-lg border border-cottage-accent bg-cottage-secondary/20 px-5 py-4 text-harbour-navy outline-none transition focus:border-harbour-accent focus:bg-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-lg uppercase tracking-[0.3em] text-cottage-primary">
                    Message
                  </label>
                  <textarea
                    name="Message"
                    value={formData.Message}
                    onChange={handleInputChange}
                    placeholder="How can we help?"
                    rows={5}
                    className="w-full rounded-lg border border-cottage-accent bg-cottage-secondary/20 px-5 py-4 text-harbour-navy outline-none transition focus:border-harbour-accent focus:bg-white"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-cottage px-10"
                >
                  Send Message
                </button>

              </form>
            </div>

            <div className="relative overflow-hidden p-10 lg:p-6 text-cottage-primary">
              <div className="relative">
                <span className="uppercase tracking-[0.4em] text-harbour-accent">
                  Contact Details
                </span>

                <h2 className="mt-6 font-serif text-4xl leading-tight">
                  {getCurrentAgentName()}
                </h2>

                <p className="mt-4 leading-7">
                  {getCurrentAgentTitle()}
                  <br />
                  {getCurrentBrokerage()}
                </p>

                <div className="mt-12 grid grid-cols-2 gap-5 ">
                  <div className="group flex min-h-40 items-start gap-6 rounded-2xl border border-cottage-primary/20 bg-white py-8 px-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">

                    {/* Icon */}
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cottage-secondary transition-colors duration-300 group-hover:bg-cottage-accent">
                      <FontAwesomeIcon
                        icon={faLocationDot}
                        className="text-2xl text-cottage-accent transition-colors duration-300 group-hover:text-white"
                      />
                    </div>


                    {/* Content */}
                    <div className="flex flex-col">
                      <p className="text-lg font-medium  text-cottage-primary">
                        Address
                      </p>

                      <p className="mt-3 max-w-sm leading-7 text-cottage-muted">
                        {constants.AGENT_ADDRESS}
                      </p>
                    </div>

                  </div>

                  <div className="group flex min-h-40 items-start gap-6 rounded-2xl border border-cottage-primary/20 bg-white py-8 px-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">

                    {/* Icon */}
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cottage-secondary transition-colors duration-300 group-hover:bg-cottage-accent">
                      <FontAwesomeIcon
                        icon={faPhone}
                        className="text-2xl text-cottage-accent transition-colors duration-300 group-hover:text-white"
                      />
                    </div>


                    {/* Content */}
                    <div className="flex flex-col">
                      <p className="text-lg font-medium  text-cottage-primary">
                        Phone
                      </p>

                      <p className="mt-3 max-w-sm leading-7 text-cottage-muted">
                        {getCurrentAgentNumber()}
                      </p>
                    </div>

                  </div>

                  <div className="group flex min-h-40 items-start gap-6 rounded-2xl border border-cottage-primary/20 bg-white py-8 px-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">

                    {/* Icon */}
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cottage-secondary transition-colors duration-300 group-hover:bg-cottage-accent">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="text-2xl text-cottage-accent transition-colors duration-300 group-hover:text-white"
                      />
                    </div>


                    {/* Content */}
                    <div className="flex flex-col">
                      <p className="text-lg font-medium  text-cottage-primary">
                        Email
                      </p>

                      <p className="mt-3 max-w-sm leading-7 text-cottage-muted">
                        {getCurrentAgentEmail()}
                      </p>
                    </div>

                  </div>

                </div>

                <div className="mt-14 border-t border-white/10 pt-8">
                  <p className="text-sm leading-7 text-white/60">
                    Dedicated to providing a refined real estate experience built on
                    trust, expertise, and lasting relationships.
                  </p>
                </div>

              </div>

            </div>

          </motion.div>
        </motion.div>
      </section>

      {popup && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />

          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-3xl shadow-2xl p-10 max-w-md w-[90%] text-center">
            <div className="w-16 h-16 rounded-full bg-summit-highlight/20 flex items-center justify-center mx-auto mb-6">
              ✓
            </div>

            <h3 className="font-heading text-3xl text-summit-primary">
              {popup.subject}
            </h3>

            <p className="text-summit-muted mt-4 leading-7">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="mt-8 bg-summit-secondary hover:bg-summit-primary transition text-white px-8 py-3 rounded-xl"
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
